import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { processImageForUpload } from '../../utils/imageProcessing';
import { createPortal } from 'react-dom';

// Image Editor Modal specific styles
const editorStyles = `
  /* Override hover styles for desktop/mobile view buttons */
  .image-editor-view-selector button {
    transition: background-color 0.2s ease !important;
  }
  .image-editor-view-selector button.active-view {
    background-color: #2563eb !important;
  }
  .image-editor-view-selector button.active-view:hover {
    background-color: #2563eb !important;
  }

  /* Prevent hover effect on close button */
  .bg-white.rounded-lg button.p-1\\.5.border.border-aquarius.rounded-full {
    transition: none !important;
  }

  .bg-white.rounded-lg button.p-1\\.5.border.border-aquarius.rounded-full:hover {
    background-color: transparent !important;
    opacity: 1 !important;
    transform: none !important;
    scale: none !important;
  }

  /* Mobile-specific styles for close button */
  @media (max-width: 768px) {
    .image-editor-modal > div:first-child > button {
      min-height: 28px !important;
      height: 28px !important;
      width: 28px !important;
      aspect-ratio: 1 !important;
      padding: 0.375rem !important;
    }
  }

  /* Prevent hover effect on Reset button */
  .bg-white.rounded-lg .flex.justify-end.mt-3.gap-2 button.mr-auto:hover,
  .bg-white.rounded-lg .flex.justify-end.mt-3.md\\:mt-6.gap-2 button.mr-auto:hover {
    background-color: var(--background-color, rgb(241 245 249)) !important;
    opacity: 1 !important;
  }

  /* Hide all grid lines from react-easy-crop */
  .reactEasyCrop_CropArea {
    border: none !important;
    color: transparent !important;
  }
  
  .reactEasyCrop_CropAreaGrid::before,
  .reactEasyCrop_CropAreaGrid::after {
    display: none !important;
  }

  /* Remove any overlay from the container */
  .reactEasyCrop_Container {
    background: none !important;
  }

  /* Remove crop area shading */
  .reactEasyCrop_Wrapper {
    background: none !important;
  }

  /* Profile photo circular preview overlay */
  .profile-photo-preview-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px dashed rgba(255, 255, 255, 0.5);
    pointer-events: none;
    z-index: 10;
  }

  /* Rule of thirds gridlines */
  .rule-of-thirds-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
  }

  .rule-of-thirds-grid::before {
    content: '';
    position: absolute;
    top: 0;
    left: 33.33%;
    width: 1px;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.5);
  }

  .rule-of-thirds-grid::after {
    content: '';
    position: absolute;
    top: 0;
    left: 66.67%;
    width: 1px;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.5);
  }

  .rule-of-thirds-grid .horizontal-line {
    position: absolute;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.5);
  }

  .rule-of-thirds-grid .horizontal-line:nth-child(1) {
    top: 33.33%;
  }

  .rule-of-thirds-grid .horizontal-line:nth-child(2) {
    top: 66.67%;
  }

  /* Clear circle with shaded overlay outside */
  .crop-container::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    background: transparent;
    border-radius: 50%;
    z-index: 2;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
    pointer-events: none;
  }

  /* Make the cropper container square */
  .crop-container {
    aspect-ratio: 1;
    max-width: 500px;
    margin: 0 auto;
    background: none !important;
  }

  /* Mobile responsiveness for the crop container */
  @media (max-width: 768px) {
    .crop-container {
      aspect-ratio: 1;
      max-width: 100%;
      max-height: 40vh;
    }
    
    /* Better mobile modal styling */
    .image-editor-modal {
      width: 95% !important;
      max-width: 400px !important;
      max-height: 95vh !important;
      margin: auto !important;
      padding: 16px !important;
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
    }
    
    /* Reduce spacing on mobile */
    .image-editor-modal .flex.justify-end {
      margin-bottom: 8px;
    }
    
    .image-editor-modal h2 {
      font-size: 1.25rem;
      margin-bottom: 8px;
    }
    
    /* Adjust controls spacing for mobile */
    .image-editor-modal .grid.grid-cols-2 {
      gap: 8px;
    }
    
    /* Make buttons more touch-friendly */
    .image-editor-modal button {
      min-height: 40px;
    }

    /* Keep close button square in mobile view */
    .image-editor-modal .flex.justify-end button.p-1\.5.border.border-aquarius.rounded-full {
      min-height: 28px;
      height: 28px;
      width: 28px;
    }
  }
`;

interface ImageEditorModalProps {
  isOpen: boolean
  onClose: () => void
  imageFile: File | null
  onSave: (editedFile: File) => void
  aspectRatio?: number
  isProfilePhoto?: boolean
}

// Helper function to validate and normalize crop dimensions
const validateCropDimensions = (
    crop: { x: number; y: number; width: number; height: number },
    imageWidth: number,
    imageHeight: number
) => {
    if (!crop || typeof crop.width !== 'number' || typeof crop.height !== 'number' ||
      typeof crop.x !== 'number' || typeof crop.y !== 'number') {
        console.error('Invalid crop dimensions received:', crop);
        return {
            x: 0,
            y: 0,
            width: imageWidth,
            height: imageHeight
        };
    }

    return {
        x: Math.round(Math.abs(crop.x)) || 0,
        y: Math.round(Math.abs(crop.y)) || 0,
        width: Math.round(Math.abs(crop.width)) || imageWidth,
        height: Math.round(Math.abs(crop.height)) || imageHeight
    };
};

// Helper function to create and configure canvas
const createCanvas = (width: number, height: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context available');
    }

    canvas.width = width;
    canvas.height = height;

    // Clear the canvas
    ctx.clearRect(0, 0, width, height);

    return { canvas, ctx };
};

// Helper function to apply transformations to context
const applyTransformations = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    flip: { horizontal: boolean; vertical: boolean },
    brightness: number,
    contrast: number,
    saturation: number
) => {
    const centerX = width / 2;
    const centerY = height / 2;

    // Save the initial context state
    ctx.save();

    // Apply flip transformations
    ctx.translate(centerX, centerY);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-centerX, -centerY);

    // Apply brightness, contrast, and saturation filters
    const brightnessValue = brightness / 100;
    const contrastValue = contrast / 100;
    const saturationValue = saturation / 100;

    // Apply the filters
    ctx.filter = `brightness(${brightnessValue}) contrast(${contrastValue}) saturate(${saturationValue})`;
};

// Helper function to create blob from canvas
const createBlobFromCanvas = (canvas: HTMLCanvasElement, mimeType: string) => {
    return new Promise<Blob>((resolve, reject) => {
        try {
            const dataUrl = canvas.toDataURL(mimeType, 0.95);

            if (!dataUrl || dataUrl === 'data:,') {
                throw new Error('Canvas produced empty data URL');
            }

            const byteString = atob(dataUrl.split(',')[1]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);

            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([ab], { type: mimeType });

            if (blob.size === 0) {
                throw new Error('Created blob has zero size');
            }

            resolve(blob);
        } catch (error) {
            reject(error);
        }
    });
};

// Validate that crop area is within image bounds
const isCropWithinBounds = (
    crop: { x: number; y: number; width: number; height: number },
    imageWidth: number,
    imageHeight: number
): boolean => {
    if (!crop) return false;

    // Check that crop is fully contained within the image
    const isValid =
    crop.x >= 0 &&
    crop.y >= 0 &&
    crop.x + crop.width <= imageWidth &&
    crop.y + crop.height <= imageHeight;

    return isValid;
};

// Main function to create final image
const createFinalImage = async (
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number },
    flip = { horizontal: false, vertical: false },
    fileName: string,
    fileType: string,
    brightness = 100,
    contrast = 100,
    saturation = 100,
    isProfilePhoto = true
) => {
    return new Promise<File>((resolve, reject) => {
        try {
            const image = new Image();
            image.crossOrigin = 'anonymous';

            image.onload = async () => {
                try {
                    // For profile photos, we use the dimensions from the cropped area
                    // This ensures consistent results regardless of the zoom level used in the preview
                    const { canvas, ctx } = createCanvas(
                        pixelCrop.width,
                        pixelCrop.height
                    );

                    // Fill with black background by default
                    ctx.fillStyle = 'black';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Apply transformations before drawing
                    applyTransformations(
                        ctx,
                        canvas.width,
                        canvas.height,
                        flip,
                        brightness,
                        contrast,
                        saturation
                    );

                    // For profile photos, we need to ensure the exact same portion shown
                    // in the circular preview is captured
                    if (isProfilePhoto) {
                        // Handle any negative coordinates (can happen with react-easy-crop)
                        // and ensure we're drawing the visible portion of the image
                        let sourceX = pixelCrop.x;
                        let sourceY = pixelCrop.y;
                        let sourceWidth = pixelCrop.width;
                        let sourceHeight = pixelCrop.height;
                        let destX = 0;
                        let destY = 0;
                        let destWidth = pixelCrop.width;
                        let destHeight = pixelCrop.height;

                        // Adjust for negative coordinates (which means the crop area extends outside the image)
                        if (sourceX < 0) {
                            const outsideRatio = Math.abs(sourceX) / sourceWidth;
                            destX = Math.round(destWidth * outsideRatio);
                            destWidth = destWidth - destX;
                            sourceWidth = sourceWidth + sourceX;
                            sourceX = 0;
                        }

                        if (sourceY < 0) {
                            const outsideRatio = Math.abs(sourceY) / sourceHeight;
                            destY = Math.round(destHeight * outsideRatio);
                            destHeight = destHeight - destY;
                            sourceHeight = sourceHeight + sourceY;
                            sourceY = 0;
                        }

                        // Handle overflow at the right/bottom edges too
                        if (sourceX + sourceWidth > image.width) {
                            const outsideRatio = (sourceX + sourceWidth - image.width) / sourceWidth;
                            sourceWidth = image.width - sourceX;
                            destWidth = Math.round(destWidth * (1 - outsideRatio));
                        }

                        if (sourceY + sourceHeight > image.height) {
                            const outsideRatio = (sourceY + sourceHeight - image.height) / sourceHeight;
                            sourceHeight = image.height - sourceY;
                            destHeight = Math.round(destHeight * (1 - outsideRatio));
                        }

                        // Draw the image with adjusted parameters to match exactly what was in the preview
                        ctx.drawImage(
                            image,
                            sourceX,
                            sourceY,
                            sourceWidth,
                            sourceHeight,
                            destX,
                            destY,
                            destWidth,
                            destHeight
                        );
                    } else {
                        // For non-profile photos, just use the cropped area directly
                        ctx.drawImage(
                            image,
                            pixelCrop.x,
                            pixelCrop.y,
                            pixelCrop.width,
                            pixelCrop.height,
                            0,
                            0,
                            canvas.width,
                            canvas.height
                        );
                    }

                    // Restore context to remove filters and transformations
                    ctx.restore();

                    // Output format handling
                    const isGif = fileType === 'image/gif';
                    const mimeType = isGif ? 'image/jpeg' : 'image/jpeg';

                    try {
                        const blob = await createBlobFromCanvas(canvas, mimeType);

                        // Create filename with appropriate extension
                        const nameWithoutExt = fileName.split('.')[0];
                        const timestamp = Date.now();
                        const newFileName = isGif
                            ? fileName
                            : `${nameWithoutExt}_${timestamp}.jpg`;

                        const file = new File([blob], newFileName, {
                            type: isGif ? fileType : mimeType
                        });

                        resolve(file);
                    } catch (blobError) {
                        reject(blobError);
                    }
                } catch (error) {
                    reject(error);
                }
            };

            image.onerror = (error: Event | string) => {
                const errorMessage = error instanceof Event ? 'Image load failed' : error;
                reject(new Error(`Failed to load image: ${errorMessage}`));
            };

            image.src = imageSrc;

            if (image.complete) {
                const event = new Event('load');
                image.dispatchEvent(event);
            }
        } catch (error) {
            reject(error);
        }
    });
};

export const ImageEditorModal = ({
    isOpen,
    onClose,
    imageFile,
    onSave,
    aspectRatio = 1,
    isProfilePhoto = true,
}: ImageEditorModalProps) => {
    // Initialize with default values
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number; y: number; width: number; height: number
  } | null>(null);
    const [brightness, setBrightness] = useState(100);
    const [contrast, setContrast] = useState(100);
    const [saturation, setSaturation] = useState(100);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [cropperReady, setCropperReady] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [mobileView, setMobileView] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Detect mobile devices
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Calculate container width on mount and when window is resized
    useEffect(() => {
        const updateContainerWidth = () => {
            // For cover photos, calculate aspect ratio based on container width
            const container = document.querySelector('.crop-container');
            if (container && !isProfilePhoto) {
                // Get the actual width of the container, considering padding and margins
                const containerStyle = window.getComputedStyle(container);
                const containerWidth = container.clientWidth -
          parseFloat(containerStyle.paddingLeft) -
          parseFloat(containerStyle.paddingRight);

                setContainerWidth(containerWidth);
            }
        };

        // Initial calculation
        if (isOpen && !isProfilePhoto) {
            // Wait for DOM to be ready
            setTimeout(updateContainerWidth, 100);
        }

        // Update on resize
        window.addEventListener('resize', updateContainerWidth);
        return () => window.removeEventListener('resize', updateContainerWidth);
    }, [isOpen, isProfilePhoto]);

    // Calculate dynamic aspect ratio for cover photo
    const dynamicAspectRatio = useMemo(() => {
        if (isProfilePhoto) {
            return aspectRatio; // Use provided aspect ratio for profile photos
        }

        // For cover photos, use container width / fixed height (150px)
        // Mobile view uses a different aspect ratio than desktop
        if (containerWidth > 0) {
            // Mobile typically has a taller, narrower aspect ratio
            if (mobileView) {
                // For mobile, we use a narrower width proportion (70% of container width)
                const mobileWidth = containerWidth * 0.7;
                const mobileRatio = mobileWidth / 150;
                return mobileRatio;
            } else {
                // For desktop, use the actual container width
                const desktopRatio = containerWidth / 150;
                return desktopRatio;
            }
        }

        // Fallback to a reasonable default if container width is not available yet
        return window.innerWidth / 150;
    }, [isProfilePhoto, aspectRatio, containerWidth, mobileView]);

    // Calculate crop size based on view mode
    const cropSize = useMemo(() => {
        if (isProfilePhoto) {
            return { width: 250, height: 250 };
        }

        if (containerWidth > 0) {
            if (mobileView) {
                // For mobile, use 70% of container width
                const mobileWidth = containerWidth * 0.7;
                return { width: mobileWidth, height: 150 };
            } else {
                // For desktop, use the actual container width
                return { width: containerWidth, height: 150 };
            }
        }

        // Fallback to window width if container width not available
        return {
            width: window.innerWidth > 768 ? Math.min(window.innerWidth * 0.8, 900) : window.innerWidth * 0.85,
            height: 150
        };
    }, [isProfilePhoto, containerWidth, mobileView]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            setBrightness(100);
            setContrast(100);
            setSaturation(100);
            setCropperReady(false);
            setImageDimensions({ width: 0, height: 0 });
            if (imageUrl) {
                // Remove the cache-busting part before revoking
                const baseUrl = imageUrl.split('#')[0];
                URL.revokeObjectURL(baseUrl);
                setImageUrl('');
            }
        }

        // Cleanup function to ensure URLs are revoked when component unmounts
        return () => {
            if (imageUrl) {
                // Remove the cache-busting part before revoking
                const baseUrl = imageUrl.split('#')[0];
                URL.revokeObjectURL(baseUrl);
            }
        };
    }, [isOpen, imageUrl]);

    // Create URL when imageFile changes
    useEffect(() => {
        if (imageFile && isOpen) {
            // If there's an existing URL, revoke it first
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }

            // Create a new URL with a timestamp to prevent caching
            const timestamp = Date.now();
            const url = URL.createObjectURL(imageFile);
            const cacheBustedUrl = `${url}#${timestamp}`;

            // Reset cropper state
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            setCropperReady(false);

            // Pre-load the image to verify it loads correctly
            const img = new Image();
            img.onload = () => {
                setImageDimensions({ width: img.width, height: img.height });
                setImageUrl(cacheBustedUrl);
                setCropperReady(true);
            };
            img.onerror = (err) => {
                // Show error to user
                alert('Failed to load image. Please try again.');
                // Clean up URL
                URL.revokeObjectURL(url);
                onClose();
            };
            img.src = url;
        }
    }, [imageFile, isOpen]);

    // Handle cropper ready event
    const onCropperReady = useCallback(() => {
        setCropperReady(true);
    }, []);

    const onCropComplete = useCallback((
        croppedArea: { x: number; y: number; width: number; height: number },
        croppedAreaPixels: { x: number; y: number; width: number; height: number }
    ) => {
        // Calculate and log the relationship between displayed crop area and pixel crop area
        const displayToPixelRatio = {
            widthRatio: croppedAreaPixels.width / croppedArea.width,
            heightRatio: croppedAreaPixels.height / croppedArea.height,
            xRatio: croppedAreaPixels.x / croppedArea.x,
            yRatio: croppedAreaPixels.y / croppedArea.y
        };

        // Store the crop area and current zoom
        setCroppedAreaPixels(croppedAreaPixels);
    }, [zoom]);

    const handleSave = async () => {
        if (!imageFile || !imageUrl) {
            const errorMsg = 'Missing required data for save. Please try again.';
            setError(errorMsg);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Always use the exact crop area pixels that the user selected
            if (!croppedAreaPixels) {
                throw new Error('No crop area selected. Please try again.');
            }

            // For profile photos, we need to get EXACTLY what was shown in the circular preview
            if (isProfilePhoto) {
                try {
                    // The croppedAreaPixels from react-easy-crop already accounts for zoom
                    // We need to use exactly what's shown in the preview for profile photos
                    const editedFile = await createFinalImage(
                        imageUrl,
                        croppedAreaPixels,
                        { horizontal: false, vertical: false },
                        imageFile.name,
                        imageFile.type,
                        brightness,
                        contrast,
                        saturation,
                        true
                    );

                    // Process for upload
                    const processedFile = await processImageForUpload(editedFile);
                    onSave(processedFile);
                    onClose();
                    return;
                } catch (error) {
                    throw error;
                }
            }

            // Create a canvas element to pre-render the image with zoom applied
            const img = new Image();
            img.crossOrigin = 'anonymous';

            // Load the image and continue processing
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('Failed to load image for zoom processing'));
                img.src = imageUrl;
            });

            // Create a canvas with the exact dimensions we want for the output
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Could not get canvas context');
            }

            // Use the actual crop dimensions for both profile and non-profile photos
            const outputWidth = croppedAreaPixels.width;
            const outputHeight = croppedAreaPixels.height;

            canvas.width = outputWidth;
            canvas.height = outputHeight;

            // Draw the image to the canvas - use exactly what's shown in the preview
            ctx.drawImage(
                img,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                outputWidth,
                outputHeight
            );

            // Apply brightness, contrast, and saturation
            if (brightness !== 100 || contrast !== 100 || saturation !== 100) {
                const imageData = ctx.getImageData(0, 0, outputWidth, outputHeight);
                const data = imageData.data;

                // Convert brightness/contrast/saturation percentages to multipliers
                const brightnessMultiplier = brightness / 100;
                const contrastMultiplier = contrast / 100;
                const saturationMultiplier = saturation / 100;

                for (let i = 0; i < data.length; i += 4) {
                    // Apply brightness
                    data[i] = data[i] * brightnessMultiplier;     // R
                    data[i + 1] = data[i + 1] * brightnessMultiplier; // G
                    data[i + 2] = data[i + 2] * brightnessMultiplier; // B

                    // Apply contrast
                    data[i] = ((data[i] - 128) * contrastMultiplier) + 128;     // R
                    data[i + 1] = ((data[i + 1] - 128) * contrastMultiplier) + 128; // G
                    data[i + 2] = ((data[i + 2] - 128) * contrastMultiplier) + 128; // B

                    // Apply saturation (simplified version)
                    if (saturationMultiplier !== 1) {
                        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
                        data[i] = avg + (data[i] - avg) * saturationMultiplier;       // R
                        data[i + 1] = avg + (data[i + 1] - avg) * saturationMultiplier; // G
                        data[i + 2] = avg + (data[i + 2] - avg) * saturationMultiplier; // B
                    }
                }

                ctx.putImageData(imageData, 0, 0);
            }

            // Convert canvas to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (blob) resolve(blob);
                        else reject(new Error('Failed to create blob from canvas'));
                    },
                    'image/jpeg',
                    0.95
                );
            });

            // Create the final file
            const nameWithoutExt = imageFile.name.split('.')[0];
            const timestamp = Date.now();
            const newFileName = `${nameWithoutExt}_${timestamp}.jpg`;
            const editedFile = new File([blob], newFileName, { type: 'image/jpeg' });

            // Process the image for upload based on size and type
            const processedFile = await processImageForUpload(editedFile);

            onSave(processedFile);
            onClose();
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : 'Failed to save image. Please try again.';
            setError(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle reset of all slider values to their defaults
    const handleReset = () => {
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setBrightness(100);
        setContrast(100);
        setSaturation(100);
    };

    // Create dynamic styles for the image transformations
    const getTransformStyles = () => {
        return `
      .reactEasyCrop_Image {
        filter: brightness(${brightness / 100}) contrast(${contrast / 100}) saturate(${saturation / 100}) !important;
      }
    `;
    };

    // If the modal is not open or no image file, don't render anything
    if (!isOpen || !imageFile) {
        return null;
    }

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <style>{editorStyles}</style>
            <style>{getTransformStyles()}</style>
            <div className="bg-white rounded-lg shadow-lg p-4 image-editor-modal w-[95%] max-w-[400px] max-h-[95vh] overflow-y-auto flex flex-col relative">
                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className="p-1.5 border border-aquarius rounded-full cursor-pointer h-7 w-7 flex items-center justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M14.9168 13.4575C15.1106 13.6512 15.2194 13.914 15.2194 14.188C15.2194 14.4619 15.1106 14.7247 14.9168 14.9184C14.7231 15.1122 14.4603 15.221 14.1864 15.221C13.9124 15.221 13.6496 15.1122 13.4559 14.9184L7.99972 9.46054L2.54183 14.9167C2.3481 15.1104 2.08534 15.2193 1.81136 15.2193C1.53738 15.2193 1.27462 15.1104 1.08089 14.9167C0.887158 14.723 0.77832 14.4602 0.77832 14.1862C0.77832 13.9123 0.887158 13.6495 1.08089 13.4558L6.53878 7.9996L1.08261 2.54171C0.888877 2.34798 0.780039 2.08522 0.780039 1.81124C0.780039 1.53726 0.888877 1.27451 1.08261 1.08077C1.27634 0.887041 1.5391 0.778203 1.81308 0.778203C2.08706 0.778203 2.34981 0.887041 2.54355 1.08077L7.99972 6.53866L13.4576 1.07991C13.6513 0.886181 13.9141 0.777344 14.1881 0.777344C14.4621 0.777344 14.7248 0.886181 14.9185 1.07991C15.1123 1.27365 15.2211 1.5364 15.2211 1.81038C15.2211 2.08436 15.1123 2.34712 14.9185 2.54085L9.46066 7.9996L14.9168 13.4575Z" fill="black" />
                        </svg>
                    </button>
                </div>
                <div className="flex justify-between items-center mb-2 md:mb-4">
                    <h2 className="text-xl font-bold">
            Edit {isProfilePhoto ? 'Profile' : 'Cover'} Photo
                    </h2>
                </div>

                {error && (
                    <div className="mb-2 md:mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Main editing area - square container */}
                <div className="mb-3 md:mb-6 flex-shrink-0">
                    <div className="relative bg-transparent overflow-hidden crop-container">
                        {cropperReady ? (
                            <>
                                <Cropper
                                    image={imageUrl}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                    onMediaLoaded={onCropperReady}
                                    showGrid={false}
                                    objectFit="contain"
                                    zoomWithScroll={true}
                                    restrictPosition={false}
                                />

                                <div className="rule-of-thirds-grid">
                                    <div className="horizontal-line" />
                                    <div className="horizontal-line" />
                                </div>
                                <div className="profile-photo-preview-overlay" />
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-500">Loading image...</p>
                            </div>
                        )}
                    </div>

                    {!isProfilePhoto && (
                        <div className="mt-2 p-2 bg-gray-100 rounded-md">
                            <p className="text-sm text-gray-700 text-center mb-1">
                The crop area shows how your cover photo will appear on <span className="font-semibold">{mobileView ? 'mobile devices' : 'desktop screens'}</span>.
                            </p>
                            <p className="text-xs text-gray-500 text-center">
                                {mobileView
                                    ? 'Mobile view shows more height and less width than desktop.'
                                    : 'Desktop view shows the full width but may zoom in more than mobile.'}
                                <br />
                Switch between views to see how your image will be cropped on different devices.
                            </p>
                        </div>
                    )}
                </div>

                {/* Controls - more compact on mobile */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
                    <div>
                        <label className="block text-sm font-medium mb-0.5 md:mb-1">Zoom</label>
                        <Slider
                            value={[zoom]}
                            min={0.5}
                            max={5}
                            step={0.1}
                            onValueChange={(value: number[]) => setZoom(value[0])}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-0.5 md:mb-1">Brightness</label>
                        <Slider
                            value={[brightness]}
                            min={50}
                            max={150}
                            step={1}
                            onValueChange={(value: number[]) => setBrightness(value[0])}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-0.5 md:mb-1">Contrast</label>
                        <Slider
                            value={[contrast]}
                            min={50}
                            max={150}
                            step={1}
                            onValueChange={(value: number[]) => setContrast(value[0])}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-0.5 md:mb-1">Saturation</label>
                        <Slider
                            value={[saturation]}
                            min={0}
                            max={200}
                            step={1}
                            onValueChange={(value: number[]) => setSaturation(value[0])}
                        />
                    </div>
                </div>

                <div className="flex justify-end mt-3 md:mt-6 gap-2">
                    <Button
                        variant="secondary"
                        onClick={handleReset}
                        className="mr-auto py-1 px-3 md:py-2 md:px-4"
                    >
                        Reset
                    </Button>
                    <Button variant="outline" onClick={onClose} disabled={isSaving} className="py-1 px-3 md:py-2 md:px-4">
            Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving} className="py-1 px-3 md:py-2 md:px-4">
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
};
